import React, { useState, useEffect, useContext, useMemo } from 'react';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// SERVICES
import { getUsersBDDInfo } from 'services/administration/usersService';
import { getUserPreferences } from 'services/tileService';

// COMPONENTS
import Modal from 'components/modalComponent';
import Grid from 'components/gridComponent';
import CardUser from 'components/administration/users/cardUserComponent';
import SearchBar from 'components/administration/searchBarComponent';

// UTILS
import { MapPreferencesToTiles } from 'utils/mapPreferencesToTiles';

// ICONS
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

function ListUsers() {
    const { addToast } = useContext(ToastContext);
    const [users, setUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userTiles, setUserTiles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;

    useEffect(() => {
        getUsersBDDInfo()
            .then((response) => {
                setUsers(response.data);
            })
            .catch((error) => {
                console.error('Erreur lors de la récupération des utilisateurs :', error);
            });
    }, []);

    // Réinitialiser la page courante lors d'une recherche
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handleOpenModal = async (user) => {
        setSelectedUser(user);

        try {
            const response = await getUserPreferences(user.USR_ID); 
            const tiles =  MapPreferencesToTiles(response.data);
            if (tiles.length === 0) {
                addToast('Aucune préférence trouvée', 'Aucune préférence trouvée pour cet utilisateur.', null, null, 'error');
                return;
            }
            setUserTiles(tiles);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Erreur lors de la récupération des préférences de l'utilisateur:", error);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
        setUserTiles([]);
    };

    const filteredUsers = useMemo(() => {
        if (!searchTerm) {
            return users;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();
        return users.filter(
            (user) =>
                user.USR_EMAILADDRESS.toLowerCase().includes(lowerSearchTerm)
        );
    }, [searchTerm, users]);

    // Calcul des utilisateurs pour la page courante
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * usersPerPage;
        return filteredUsers.slice(startIndex, startIndex + usersPerPage);
    }, [filteredUsers, currentPage]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flexShrink: 0 }}>
                <SearchBar
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher email..."
                />
            </div>
            <div
                className="space-y-4 overflow-y-auto mt-4 p-2"
                style={{ maxHeight: '700px', flexGrow: 1 }}
            >
                {paginatedUsers.map((user) => (
                    <CardUser
                        key={user.USR_ID}
                        user={user}
                        handleOpenModal={handleOpenModal}
                    />
                ))}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center items-center mt-4 gap-2">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded hover:bg-noir/10 dark:hover:bg-gris/40 disabled:opacity-50 cursor-pointer"
                >
                    <ChevronLeft size={20} className="text-noir dark:text-blanc" />
                </button>
                <span className="mx-2">
                    Page {currentPage} sur {totalPages}
                </span>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded hover:bg-noir/10 dark:hover:bg-gris/40 disabled:opacity-50 cursor-pointer"
                >
                    <ChevronRight size={20} className="text-noir dark:text-blanc" />
                </button>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <Modal onClose={handleCloseModal}>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Interface de {selectedUser?.USR_EMAILADDRESS}</h2>
                        <X size={32} onClick={handleCloseModal} className="cursor-pointer hover:text-red-500" />
                    </div>
                    <div className="flex justify-center">
                        {userTiles && userTiles.length > 0 ? (
                            <Grid tiles={userTiles} isViewing={true} />
                        ) : (
                            <p>Aucune préférence trouvée pour cet utilisateur.</p>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default ListUsers;